import { Routes } from '@angular/router';

import { AuthGuard, HasScopeGuard } from '@gan/common/feature-auth';
import { provideStations } from '@gan/common/data-stations';
import { SetTitleGuard } from '@gan/shared/ui-title';
import { provideDeviations } from '@gan/common/data-deviations';

import { AccessScopesUnion } from '@gan/core/model-access-scopes';
import { provideMaterialOverview } from '@gan/common/data-material-overview';
import { provideSpennEarnings } from '@gan/common/data-spenn-earnings';

export const ROUTES: Routes = [
  {
    path: '',
    loadComponent: () =>
      import('./layout/protected/protected-layout.component').then(
        ({ ProtectedLayoutComponent }) => ProtectedLayoutComponent
      ),
    canActivate: [AuthGuard],
    children: [
      {
        path: '',
        redirectTo: 'incident-overview',
        pathMatch: 'full'
      },
      {
        path: 'monitor',
        data: {
          someScopeRequired: ['Admin', 'Monitor'] as AccessScopesUnion[],
          title: 'MENU.MONITOR'
        },
        canActivate: [SetTitleGuard, HasScopeGuard],
        loadComponent: () =>
          import('./pages/monitor/monitor.component').then(({ MonitorComponent }) => MonitorComponent)
      },
      {
        path: 'train-composition',
        data: {
          someScopeRequired: ['Admin', 'Rooster', 'TrainServiceRead'] as AccessScopesUnion[],
          title: 'MENU.TRAIN_COMPOSITION'
        },
        canActivate: [SetTitleGuard, HasScopeGuard],
        providers: [provideStations(), provideMaterialOverview()],
        loadChildren: () =>
          import('./pages/train-composition/routes').then(({ TRAIN_COMPOSITION_ROUTES }) => TRAIN_COMPOSITION_ROUTES)
      },
      {
        path: 'rtv',
        data: {
          someScopeRequired: ['Admin', 'RTV'] as AccessScopesUnion[],
          title: 'MENU.RTV'
        },
        canActivate: [SetTitleGuard, HasScopeGuard],
        providers: [provideStations(), provideDeviations()],
        loadComponent: () =>
          import('./pages/rtv-page/rtv-page.component').then(({ RtvPageComponent }) => RtvPageComponent)
      },
      {
        path: 'material-overview',
        data: {
          someScopeRequired: ['Admin', 'MaterialRead'] as AccessScopesUnion[],
          title: 'MENU.MATERIAL_OVERVIEW'
        },
        canActivate: [SetTitleGuard, HasScopeGuard],
        providers: [provideStations(), provideMaterialOverview()],
        loadComponent: () =>
          import('./pages/material-overview/material-overview.component').then(
            ({ MaterialOverviewComponent }) => MaterialOverviewComponent
          )
      },
      {
        path: 'operation-log',
        data: {
          someScopeRequired: ['Admin'] as AccessScopesUnion[],
          title: 'MENU.OPERATION_LOG'
        },
        canActivate: [SetTitleGuard, HasScopeGuard],
        providers: [provideStations()],
        loadComponent: () =>
          import('./pages/operation-log/operation-log.component').then(
            ({ OperationLogComponent }) => OperationLogComponent
          )
      },
      {
        path: 'system-config',
        data: {
          someScopeRequired: [
            'Admin',
            'SystemConfigWrite',
            'ResourceManagerSync',
            'TiosReasonCodesExportRead'
          ] as AccessScopesUnion[],
          title: 'MENU.CONFIG'
        },
        canActivate: [SetTitleGuard, HasScopeGuard],
        loadComponent: () =>
          import('./pages/system-config/system-config.component').then(
            ({ SystemConfigComponent }) => SystemConfigComponent
          )
      },
      {
        path: 'incident-overview',
        data: {
          someScopeRequired: ['Admin', 'SmartLogRead', 'SmartLogDeviationRead'],
          title: 'MENU.INCIDENT_OVERVIEW'
        },
        canActivate: [SetTitleGuard, HasScopeGuard],
        loadChildren: () =>
          import('@gan/gan-ops/feature-incident-overview').then(
            ({ INCIDENT_OVERVIEW_ROUTES }) => INCIDENT_OVERVIEW_ROUTES
          )
      },
      {
        path: 'incident/:id',
        redirectTo: 'incident-overview/incidents/:id',
        pathMatch: 'full'
      },
      {
        path: 'admin',
        data: {
          someScopeRequired: [
            'AdminDashboard',
            'AdminLogs',
            'AdminLookups',
            'AdminIntegrations'
          ] as AccessScopesUnion[],
          title: 'MENU.ADMIN'
        },
        canActivate: [SetTitleGuard, HasScopeGuard],
        loadChildren: () => import('@gan/gan-ops/feature-admin').then(({ ADMIN_ROUTES }) => ADMIN_ROUTES)
      },
      {
        path: 'plan-team',
        data: {
          someScopeRequired: ['AbsenceRead'] as AccessScopesUnion[], // Replace with PlanTeam
          title: 'MENU.ABSENCE'
        },
        canActivate: [SetTitleGuard, HasScopeGuard],
        loadComponent: () => import('@gan/gan-ops/feature-absence').then(({ AbsenceComponent }) => AbsenceComponent)
      },
      {
        path: 'passengers-list',
        data: {
          title: 'MENU.PASSENGER_LIST',
          someScopeRequired: ['MyJourneyRead'] as AccessScopesUnion[]
        },
        canActivate: [SetTitleGuard, HasScopeGuard],
        loadChildren: () =>
          import('@gan/gan-ops/feature-train-service-passengers-list').then(
            ({ PASSENGER_LIST_ROUTES }) => PASSENGER_LIST_ROUTES
          )
      },
      {
        path: 'spenn-management',
        data: {
          someScopeRequired: ['Admin', 'SpennRead', 'SpennWrite'] as AccessScopesUnion[],
          title: 'MENU.SPENN_MANAGEMENT'
        },
        canActivate: [SetTitleGuard, HasScopeGuard],
        providers: [provideSpennEarnings()],
        loadComponent: () =>
          import('@gan/gan-ops/feature-spenn-management').then(
            ({ SpennManagementComponent }) => SpennManagementComponent
          )
      },
      {
        path: 'not-found',
        loadComponent: () =>
          import('./layout/not-found/not-found.component').then(({ NotFoundComponent }) => NotFoundComponent)
      }
    ]
  },
  {
    path: '**',
    redirectTo: 'train-composition/today'
  }
];

